/**
 * Component Registry for American Landmark Theme
 * 
 * Extends the base preferred theme registry with American Landmark specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-preferred/registry';

import { Hero } from './components/Hero';
import { Header } from './components/Header';
import { HomeIntroSection } from './components/HomeIntroSection';
import { HomeGalleryCarousel } from './components/HomeGalleryCarousel';
import { AmenitiesHomeSection } from './components/AmenitiesHomeSection';
import { HomeFeaturesSplit } from './components/HomeFeaturesSplit';
import { HomeGalleryGrid } from './components/HomeGalleryGrid';
import { NeighborhoodCTAHomeSection } from './components/NeighborhoodCTAHomeSection';
import { NeighborhoodHomeSection } from './components/NeighborhoodHomeSection';
import { ReviewsCallout } from './components/ReviewsCallout';
import { Footer } from './components/Footer';
import { AmenitiesList } from './components/AmenitiesList';
import { PetPolicyCallout } from './components/PetPolicyCallout';
import { NeighborhoodHero } from './components/NeighborhoodHero';
import { PageBlurb } from './components/PageBlurb';
import { FavoritePlaces } from './components/FavoritePlaces';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { ApplyNowCTA } from './components/ApplyNowCTA';
import { AmenitiesHero } from './components/AmenitiesHero';
import { ResidentAccessHero } from './components/ResidentAccessHero';
import { ResidentAccessIntro } from './components/ResidentAccessIntro';
import { ResidentAccess } from './components/ResidentAccess';
import { FloorPlansHero } from './components/FloorPlansHero';
import { PricingDisclaimer } from './components/PricingDisclaimer';
import { ApartmentSync } from './components/ApartmentSync';
import { CheckAffordability } from './components/CheckAffordability';
import { ApplyNowHero } from './components/ApplyNowHero';
import { RealpageOLL } from './components/RealpageOLL';
import { PromoPopup } from './components/PromoPopup';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { GalleryHero } from './components/GalleryHero';
import { ScheduleATourHero } from './components/ScheduleATourHero';
import { PetPolicyHero } from './components/PetPolicyHero';
import { VirtualTourHero } from './components/VirtualTourHero';
import { ContactHero } from './components/ContactHero';
import { ReviewsHero } from './components/ReviewsHero';
import { ThankYouHero } from './components/ThankYouHero';
import { PrivacyPolicyHero } from './components/PrivacyPolicyHero';
import { SitemapHero } from './components/SitemapHero';
import { SitemapContent } from './components/SitemapContent';
import { AccessibilityHero } from './components/AccessibilityHero';

/**
 * American Landmark specific component overrides.
 * These components have been customized for the American Landmark brand.
 */
const americanlandmarkOverrides: Partial<ComponentRegistry> = {
  Hero,
  Header,
  HomeIntroSection,
  HomeGalleryCarousel,
  AmenitiesHomeSection,
  HomeFeaturesSplit,
  HomeGalleryGrid,
  NeighborhoodCTAHomeSection,
  NeighborhoodHomeSection,
  ReviewsCallout,
  Footer,
  AmenitiesList,
  PetPolicyCallout,
  NeighborhoodHero,
  PageBlurb,
  FavoritePlaces,
  NeighborhoodMap,
  ApplyNowCTA,
  AmenitiesHero,
  ResidentAccessHero,
  ResidentAccessIntro,
  ResidentAccess,
  FloorPlansHero,
  PricingDisclaimer,
  ApartmentSync,
  CheckAffordability,
  ApplyNowHero,
  RealpageOLL,
  PromoPopup,
  SpecialOfferBanner,
  VirtualTourEmbed,
  GalleryHero,
  ScheduleATourHero,
  PetPolicyHero,
  VirtualTourHero,
  ContactHero,
  ReviewsHero,
  ThankYouHero,
  PrivacyPolicyHero,
  SitemapHero,
  SitemapContent,
  AccessibilityHero,
};

/**
 * Final merged registry for American Landmark properties.
 * Inherits all components from the preferred theme, with American Landmark overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...americanlandmarkOverrides,
};

export default registry;
